import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Chip,
  FormControl,
  Select,
  Typography,
  Checkbox,
  ListItemText,
  Button,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { areSameIds } from '../helpers/numbers';

const GenDialogMultiSelectDropdown = ({
  options = [],
  disabled = false,
  selectedOptions = [],
  onChange,
  optionIdKey = 'id',
  optionLabelKey = 'name',
  labelPrefix = '',
  title = 'Select Options',
  dataTestId = 'gen-dialog-multi-select-dropdown',
  placeholderText = 'Search...',
  enableSelectAll = true,
  showCloseIcon = false,
  entity = 'Option',
  size = 'small',
  error = false,
  helperText = '',
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [disableSelectAll, setDisableSelectAll] = useState(false);
  const [chosenOptions, setChosenOptions] = useState([]);
  const [initialOptionIds, setInitialOptionIds] = useState([]);

  const optionsWithKeys = useMemo(
    () =>
      options.map(opt => ({
        ...opt,
        _id: opt[optionIdKey],
        _label: labelPrefix
          ? `${opt[labelPrefix]} - ${opt[optionLabelKey]}`
          : opt[optionLabelKey],
      })),
    [options, optionIdKey, optionLabelKey, labelPrefix]
  );

  const filteredOptions = useMemo(
    () =>
      optionsWithKeys.filter(opt =>
        opt._label.toLowerCase().includes(searchText.toLowerCase())
      ),
    [optionsWithKeys, searchText]
  );

  const selectedIds = useMemo(
    () => selectedOptions.filter(Boolean),
    [selectedOptions]
  );

  const chosenOptionIds = useMemo(
    () => chosenOptions.map(opt => opt._id),
    [chosenOptions]
  );

  const sortedFilteredOptions = useMemo(() => {
    if (!filteredOptions.length) return [];

    return [...filteredOptions].sort((a, b) => {
      const aSelected = chosenOptionIds.includes(a._id);
      const bSelected = chosenOptionIds.includes(b._id);

      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  }, [filteredOptions, chosenOptionIds]);

  useEffect(() => {
    if (open) {
      const selected = optionsWithKeys.filter(opt =>
        selectedOptions.includes(opt._id)
      );
      setChosenOptions(selected);
      setInitialOptionIds(selectedOptions);
    }
  }, [open, optionsWithKeys, selectedOptions]);

  const hasChanged = useMemo(
    () => !areSameIds(chosenOptionIds, initialOptionIds),
    [chosenOptionIds, initialOptionIds]
  );

  const allVisibleSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every(opt => chosenOptionIds.includes(opt._id));

  const handleSelectAll = () => {
    const visibleIds = filteredOptions.map(opt => opt._id);
    const allSelected = visibleIds.every(id => chosenOptionIds.includes(id));

    if (allSelected) {
      setChosenOptions(
        chosenOptions.filter(opt => !visibleIds.includes(opt._id))
      );
    } else {
      const toAdd = optionsWithKeys.filter(
        opt =>
          visibleIds.includes(opt._id) && !chosenOptionIds.includes(opt._id)
      );
      setChosenOptions([...chosenOptions, ...toAdd]);
    }
  };

  const handleOptionToggle = id => {
    const exists = chosenOptionIds.includes(id);
    if (exists) {
      setChosenOptions(chosenOptions.filter(opt => opt._id !== id));
    } else {
      const opt = optionsWithKeys.find(o => o._id === id);
      setChosenOptions([...chosenOptions, opt]);
    }
  };

  const handleSave = () => {
    onChange(chosenOptions);
    setOpen(false);
    setSearchText('');
  };

  const handleCancel = () => {
    setChosenOptions(
      optionsWithKeys.filter(opt => selectedOptions.includes(opt._id))
    );
    setOpen(false);
    setSearchText('');
  };

  const handleClearSelection = e => {
    e.stopPropagation();
    onChange([]);
  };

  const shouldFloat = open || selectedIds.length > 0;

  return (
    <>
      {/* Trigger */}
      <FormControl
        fullWidth
        size={size}
        error={error}
        sx={{ position: 'relative' }}
      >
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            left: '12px',
            top: '13px',
            zIndex: 1000,
            padding: shouldFloat ? '0 5px' : '0 5px 0 2.5px',
            backgroundColor: 'white',
            pointerEvents: 'none',
            lineHeight: 1,
            transition:
              'transform 0.3s ease, font-size 0.3s ease, color 0.3s ease',
            transform: shouldFloat ? 'translateY(-18px)' : 'translateY(0)',
            fontSize: shouldFloat ? '12px' : '16px',
            color: shouldFloat ? '#007dffff' : '#746666',
            fontWeight: 'normal',
          }}
        >
          {title}
        </Typography>
        <Select
          multiple
          displayEmpty
          value={selectedIds}
          onClick={() => {
            if (!disabled) {
              setOpen(true);
            }
          }}
          disabled={disabled}
          readOnly
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 1,
          }}
          renderValue={selected => {
            if (!selected.length) return null;
            return (
              <Chip
                label={`${selected.length} ${entity}${
                  selected.length === 1 ? '' : 's'
                }`}
                size="small"
                data-testid="selection-chip"
                onDelete={handleClearSelection}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  color: 'rgba(0, 0, 0, 0.87)',
                  height: 24,
                  borderRadius: '50px',
                }}
              />
            );
          }}
          data-testid={dataTestId}
        />
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ marginLeft: '14px', marginTop: '3px' }}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>

      {/* Dialog */}
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{title}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {enableSelectAll && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allVisibleSelected}
                      onChange={handleSelectAll}
                      disabled={disableSelectAll}
                    />
                  }
                  label="Select All"
                />
              )}
              {showCloseIcon && (
                <IconButton
                  size="small"
                  data-testid="close-button"
                  onClick={handleCancel}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 0,
          }}
        >
          <Box
            className="sticky-wrapper"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <TextField
              size="small"
              placeholder={placeholderText}
              value={searchText}
              onChange={e => {
                setSearchText(e.target.value);
                setDisableSelectAll(true);
              }}
              onBlur={() => setDisableSelectAll(false)}
              fullWidth
            />
          </Box>

          <List dense>
            {sortedFilteredOptions.length === 0 ? (
              <Typography sx={{ p: 2 }} color="text.secondary">
                No options available
              </Typography>
            ) : (
              sortedFilteredOptions.map(option => (
                <ListItemButton
                  key={option._id}
                  dense
                  onClick={() => handleOptionToggle(option._id)}
                >
                  <ListItemIcon>
                    <Checkbox checked={chosenOptionIds.includes(option._id)} />
                  </ListItemIcon>
                  <ListItemText
                    primary={option._label}
                    primaryTypographyProps={{ fontSize: '16px' }}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'flex-end', gap: 3, padding: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanged}
          >
            Save
          </Button>
          {!showCloseIcon && (
            <Button
              data-testid="cancel-button"
              variant="contained"
              color="error"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

GenDialogMultiSelectDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  optionIdKey: PropTypes.string.isRequired,
  optionLabelKey: PropTypes.string.isRequired,
  title: PropTypes.string,
  dataTestId: PropTypes.string,
  placeholderText: PropTypes.string,
  enableSelectAll: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  entity: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  labelPrefix: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

export default GenDialogMultiSelectDropdown;
